import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { BRANDING } from '../../config/branding';
import {
  Body,
  CardGrid,
  ConfidenceCard,
  DocSection,
  Fill,
  Hero,
  IconItem,
  IconRow,
  InfoCard,
  Main,
  Page,
  TipCard,
  TipGrid,
  Toc,
  TocLink,
  ToolList,
  Track,
} from './styles';

const SECTION_IDS = ['overview', 'confidence', 'research-tools', 'tips'];

const SystemDocs = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const scrollToSection = sectionId => {
    const element = document.getElementById(sectionId);
    if (!element) return;

    const offset = 90;
    const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top, behavior: 'smooth' });
    setActiveSection(sectionId);
  };

  useEffect(() => {
    const onScroll = () => {
      const pointer = window.scrollY + 160;
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (pointer >= el.offsetTop && pointer < el.offsetTop + el.offsetHeight) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Page>
      <Helmet>
        <title>{`System Docs | ${BRANDING.appName}`}</title>
        <meta
          name="description"
          content="Understand predictions, PW Rating scoring, and research workflow in Proven Winner Predict."
        />
      </Helmet>

      <Hero>
        <div className="badge"><i className="fas fa-book-open" /> System Documentation</div>
        <h1>How to Use {BRANDING.appName}</h1>
        <p>
          Learn how game predictions are formed, what Proven Line and market context mean, and how to use PW Rating with the
          rest of the card to make stronger decisions.
        </p>
        <IconRow>
          <IconItem><i className="fas fa-chart-line" /><span>Predictions</span></IconItem>
          <IconItem><i className="fas fa-brain" /><span>PW Rating</span></IconItem>
          <IconItem><i className="fas fa-tools" /><span>Research</span></IconItem>
          <IconItem><i className="fas fa-layer-group" /><span>Windows</span></IconItem>
        </IconRow>
      </Hero>

      <Body>
        <Toc>
          <h4><i className="fas fa-list" /> Contents</h4>
          <TocLink href="#overview" $active={activeSection === 'overview'} onClick={e => { e.preventDefault(); scrollToSection('overview'); }}>
            <i className="fas fa-lightbulb" /> Understanding the System
          </TocLink>
          <TocLink href="#confidence" $active={activeSection === 'confidence'} onClick={e => { e.preventDefault(); scrollToSection('confidence'); }}>
            <i className="fas fa-percent" /> PW Rating Scores
          </TocLink>
          <TocLink href="#research-tools" $active={activeSection === 'research-tools'} onClick={e => { e.preventDefault(); scrollToSection('research-tools'); }}>
            <i className="fas fa-magnifying-glass-chart" /> Research Tools
          </TocLink>
          <TocLink href="#tips" $active={activeSection === 'tips'} onClick={e => { e.preventDefault(); scrollToSection('tips'); }}>
            <i className="fas fa-graduation-cap" /> Tips For Success
          </TocLink>
        </Toc>

        <Main>
          <DocSection id="overview">
            <h2><i className="fas fa-lightbulb" /> Understanding the System</h2>
            <p>
              Each matchup is built as a workflow: prediction output first, then PW Rating scoring, then market and context
              validation. You are meant to evaluate alignment across windows, not rely on one metric in isolation.
            </p>
            <CardGrid>
              <InfoCard>
                <div className="ico"><i className="fas fa-calculator" /></div>
                <h3>Game Predictions</h3>
                <p>
                  The model generates projected scores and winner direction from matchup inputs such as team strength,
                  efficiency profile, recent form, and game context.
                </p>
              </InfoCard>
              <InfoCard>
                <div className="ico"><i className="fas fa-percent" /></div>
                <h3>PW Rating Layer</h3>
                <p>
                  PW Rating estimates how reliable the model output has been in historically similar game environments.
                  Use it to rank opportunities, not to force volume.
                </p>
              </InfoCard>
              <InfoCard>
                <div className="ico"><i className="fas fa-chart-column" /></div>
                <h3>Market Context</h3>
                <p>
                  Proven Line and line windows show how model view compares with market pricing. Differences can indicate
                  potential value when other context windows support the same side.
                </p>
              </InfoCard>
            </CardGrid>
          </DocSection>

          <DocSection id="confidence">
            <h2><i className="fas fa-brain" /> PW Rating Scores</h2>
            <p>
              PW Rating is a guidance signal from historical behavior in similar environments. It is not a win probability and
              not a guarantee. Treat it as a ranking signal for quality and risk.
            </p>
            <ConfidenceCard>
              <div className="row"><span>Model PW Rating</span><strong>84%</strong></div>
              <Track><Fill $w="84%" /></Track>
              <div className="row"><span>Spread PW Rating</span><strong>71%</strong></div>
              <Track><Fill $w="71%" /></Track>
              <p>
                Best practice: act when PW Rating, model direction, and market context are aligned. If PW Rating is high but
                factors or market windows disagree, treat the spot as lower conviction.
              </p>
            </ConfidenceCard>
          </DocSection>

          <DocSection id="research-tools">
            <h2><i className="fas fa-magnifying-glass-chart" /> Research Tools</h2>
            <p>
              Multi-window game cards let you inspect power ranks, line movement, public percentages, key factors, totals,
              injuries, rosters, trends, and schedules in one flow. Use them in sequence to reduce bias.
            </p>
            <ToolList>
              <li><i className="fas fa-check-circle" /> Step 1: Read Prediction window for projected score and side.</li>
              <li><i className="fas fa-check-circle" /> Step 2: Compare Proven Line and Lines windows for model-vs-market context.</li>
              <li><i className="fas fa-check-circle" /> Step 3: Validate with Factors, Injuries, Roster, and Schedule windows.</li>
              <li><i className="fas fa-check-circle" /> Step 4: Use PW Rating to prioritize only the cleanest alignments.</li>
            </ToolList>
          </DocSection>

          <DocSection id="tips">
            <h2><i className="fas fa-graduation-cap" /> Tips For Success</h2>
            <TipGrid>
              <TipCard>
                <h4>1. Build A Daily Workflow</h4>
                <p>Review windows in the same order each day (Prediction -> Market -> Context -> PW Rating) to stay consistent.</p>
              </TipCard>
              <TipCard>
                <h4>2. Bet Fewer, Better Spots</h4>
                <p>Wait for alignment between model output, PW Rating, and market context instead of forcing action on every game.</p>
              </TipCard>
              <TipCard>
                <h4>3. Track Outcomes</h4>
                <p>Track selections with reasons by window so you can identify which signals help you most over time.</p>
              </TipCard>
            </TipGrid>
          </DocSection>
        </Main>
      </Body>
    </Page>
  );
};

export default SystemDocs;
